import { Controller } from "@hotwired/stimulus"

// Drives the always-visible hint below a password field. Supports two modes:
//
//   mode: "basic"      – student rules: min/max length only
//   mode: "complexity" – teacher/admin/superadmin rules: length + letter/number/symbol
//
// Messages are provided via data attributes so the template owns copy.
// The `submit` target is disabled until the password passes every check.
export default class extends Controller {
  static targets = ["input", "hint", "hintText", "hintIcon", "successIcon", "submit"]
  static values = {
    mode: { type: String, default: "basic" },
    minLength: { type: Number, default: 8 },
    maxLength: { type: Number, default: 70 },
    initialMessage: String,
    tooLongMessage: String,
    looksGoodMessage: String,
    // Basic mode (length-only)
    tooShortMessage: String,
    // Complexity mode (teacher) – message per missing-requirement combination
    tooShortMissingNumberSymbolMessage: String,
    tooShortMissingNumberMessage: String,
    tooShortMissingSymbolMessage: String,
    tooShortOnlyMessage: String,
    missingNumberSymbolMessage: String,
    missingNumberMessage: String,
    missingSymbolMessage: String
  }

  connect() {
    this.update()
  }

  update() {
    const value = this.inputTarget.value || ""
    const result = this.#evaluate(value)

    this.#renderHint(result)
    this.#setSubmitEnabled(result.valid)
  }

  // --- private ---

  #evaluate(value) {
    if (value.length === 0) {
      return { valid: false, tone: "hint", message: this.initialMessageValue }
    }

    if (value.length > this.maxLengthValue) {
      return { valid: false, tone: "hint", message: this.tooLongMessageValue }
    }

    if (this.modeValue === "complexity") {
      return this.#evaluateComplexity(value)
    }

    return this.#evaluateBasic(value)
  }

  #evaluateBasic(value) {
    if (value.length < this.minLengthValue) {
      return { valid: false, tone: "hint", message: this.tooShortMessageValue }
    }
    return { valid: true, tone: "success", message: this.looksGoodMessageValue }
  }

  #evaluateComplexity(value) {
    const tooShort = value.length < this.minLengthValue
    const missingNumber = !/\d/.test(value)
    const missingSymbol = !/[^a-zA-Z0-9]/.test(value)

    if (!tooShort && !missingNumber && !missingSymbol) {
      return { valid: true, tone: "success", message: this.looksGoodMessageValue }
    }

    let message
    if (tooShort && missingNumber && missingSymbol) {
      message = this.tooShortMissingNumberSymbolMessageValue
    } else if (tooShort && missingNumber) {
      message = this.tooShortMissingNumberMessageValue
    } else if (tooShort && missingSymbol) {
      message = this.tooShortMissingSymbolMessageValue
    } else if (tooShort) {
      message = this.tooShortOnlyMessageValue
    } else if (missingNumber && missingSymbol) {
      message = this.missingNumberSymbolMessageValue
    } else if (missingNumber) {
      message = this.missingNumberMessageValue
    } else {
      message = this.missingSymbolMessageValue
    }

    return { valid: false, tone: "hint", message }
  }

  #renderHint({ tone, message }) {
    if (!this.hasHintTarget) return

    const isSuccess = tone === "success"

    this.hintTarget.classList.remove("hidden")
    this.hintTarget.classList.toggle("text-content-positive", isSuccess)
    this.hintTarget.classList.toggle("text-content-secondary", !isSuccess)

    if (this.hasHintTextTarget) {
      this.hintTextTarget.textContent = message || ""
    }

    if (this.hasHintIconTarget) this.hintIconTarget.classList.toggle("hidden", isSuccess)
    if (this.hasSuccessIconTarget) this.successIconTarget.classList.toggle("hidden", !isSuccess)
  }

  #setSubmitEnabled(enabled) {
    if (!this.hasSubmitTarget) return
    this.submitTarget.disabled = !enabled
    this.submitTarget.setAttribute("aria-disabled", (!enabled).toString())
  }
}
